import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { ADMIN_REGISTRY_TOKEN, AdminGroupKey, findAdminGroup } from '@/core/layout/admin-registry';
import { MastheadContext } from '@/core/layout/masthead-context.service';

interface Breadcrumb {
    label: string;
    url: string;
}

/**
 * Breadcrumb trail built from the activated route's `data.breadcrumb` values,
 * root to leaf, skipping a crumb equal to its parent's (an empty-path child
 * inherits the parent's data, so a page without its own label would repeat
 * it). A page-declared {@link MastheadContext.crumb} is appended as a final
 * crumb (SCRUM-595): "Apps › Gemba › Completed Walks › Line 4 walk".
 *
 * A route carrying `data.adminGroup` (SCRUM-621) gets its group's label from
 * the admin registry as a crumb before its own, and its own crumb is skipped
 * when it merely repeats the group: "Administration › Teams › Shifts",
 * "Administration › Teams".
 */
@Component({
    selector: '[app-breadcrumb]',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <nav class="layout-breadcrumb">
            <ol>
                <li><i class="pi pi-home"></i></li>
                @for (item of breadcrumbs(); track $index) {
                    <li><i class="pi pi-angle-right"></i></li>
                    <li>
                        <span>{{ item.label }}</span>
                    </li>
                }
                @if (crumb(); as crumb) {
                    <li><i class="pi pi-angle-right"></i></li>
                    <li>
                        <span data-testid="breadcrumb-dynamic-crumb">{{ crumb }}</span>
                    </li>
                }
            </ol>
        </nav>
        <div class="layout-breadcrumb-buttons">
            <!-- This section is on the right side of the breadcrumbs and would be useful for page actions -->
            <!-- <button pButton pRipple type="button" icon="pi pi-cloud-upload" class="p-button-rounded p-button-text p-button-plain"></button> -->
            <!-- <button pButton pRipple type="button" icon="pi pi-bookmark" class="p-button-rounded p-button-text p-button-plain"></button> -->
            <!-- <button pButton pRipple type="button" icon="pi pi-power-off" class="p-button-rounded p-button-text p-button-plain"></button> -->
        </div>
    `,
    host: {
        class: 'layout-breadcrumb-container'
    }
})
export class AppBreadcrumb {
    private readonly router = inject(Router);
    private readonly adminRegistry = inject(ADMIN_REGISTRY_TOKEN);

    /** Dynamic last crumb set by the current page; reset by the context on every navigation. */
    protected readonly crumb = inject(MastheadContext).crumb;

    protected readonly breadcrumbs = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => this.buildBreadcrumbs()),
            startWith(this.buildBreadcrumbs()),
        ),
        { requireSync: true },
    );

    private buildBreadcrumbs(): Breadcrumb[] {
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(this.router.routerState.snapshot.root, [], breadcrumbs);
        return breadcrumbs;
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]): void {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb = route.data['breadcrumb'];
        const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;
        const group = route.data['adminGroup'] ? findAdminGroup(route.data['adminGroup'] as AdminGroupKey, this.adminRegistry) : null;

        if (group && breadcrumbs[breadcrumbs.length - 1]?.label !== group.label) {
            breadcrumbs.push({ label: group.label, url: group.home });
        }

        if (breadcrumb && breadcrumb !== parentBreadcrumb && breadcrumb !== group?.label) {
            breadcrumbs.push({
                label: route.data['breadcrumb'],
                url: '/' + routeUrl.join('/')
            });
        }

        if (route.firstChild) {
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }
}
